// @file User accounts store
import initialUser from '@@/bits/current_user'
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { currentHostname } from '@@/bits/location'
import { buildUserIdCacheKey, fetchCachedQuery } from '@@/bits/query_client'
import { NATIVE_HOST } from '@@/bits/url'
import { displayNameForUser } from '@@/bits/user_model'
import { Library as LibraryApi } from '@@/dashboard/padlet_api'
import { FetchJsonStatus } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { fetchJson } from '@@/surface/api_fetch'
import type {
  AccountKey,
  AccountMenuItem,
  JsonApiData,
  JsonApiResponse,
  Library,
  LibraryId,
  Tenant,
  TenantId,
  User,
  UserId,
} from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { sortBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const NATIVE_TENANT_ID = 1
const DEFAULT_AVATAR = 'https://padlet.net/avatars/tenant_avatar.webp'

export const useUserAccountsStore = defineStore('userAccountsStore', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  function genericFetchError(payload: { error: any; source: string }): void {
    captureNonNetworkFetchError(payload.error, { source: payload.source })
    globalSnackbarStore.genericFetchError()
  }

  /**
   * ==================== PERSONAL ACCOUNTS ====================
   */

  // user is always expected to have a defined value, `window.ww.vueStartingState.user` should be set to the window object on the server side.
  const user = ref<User>(initialUser)
  const usersById = ref<Record<UserId, User>>({})
  const tenantsById = ref<Record<TenantId, Tenant>>({})

  const currentUser = computed((): User => {
    return usersById.value[user.value.id] ?? user.value
  })
  const currentTenant = computed((): Tenant | null => {
    if (Object.keys(tenantsById.value).length === 0) return null
    if (currentUser.value == null) return null
    return tenantsById.value[currentUser.value.tenant_id]
  })
  const usersArray = computed((): User[] => Object.values(usersById.value))
  const accountsMenuList = computed((): AccountMenuItem[] => {
    const currentDomainName = currentHostname()
    const menuItems: AccountMenuItem[] = []

    // Don’t show tenant accounts in padlet.com dashboard
    // And don’t show non-tenant accounts in padlet.org dashboard

    usersArray.value
      .filter((user) => user.tenant_id === currentTenant.value?.id && user.visible)
      .forEach((user) => {
        const key: AccountKey = { type: 'user', id: user.id }
        const tenantId: TenantId = user.tenant_id
        const tenant: Tenant = tenantsById.value[tenantId]
        const tenantDomainName: string = tenant?.domain_name ?? NATIVE_HOST
        const isSameDomain = currentDomainName === tenantDomainName
        const isNative = tenantId === NATIVE_TENANT_ID
        const isLibrary = false
        const libraryId = null
        const displayName: string = isNative ? displayNameForUser(user) : tenant?.name ?? ''
        const url = `https://${tenantDomainName}/dashboard`
        const username: string = user.username ?? ''
        const wallsUsed: number = user.quota.walls_used
        const wallsLimit: number = user.quota.walls_limit
        const remainingWallsQuota: number = user.quota.walls_limit - user.quota.walls_used
        const canMakeWall = true
        const membershipTier = user.membership_tier

        // if no uploaded tenant logo, logo is default padlet logo
        let logo = tenant?.assets != null ? (tenant.assets.home_logo as string) : DEFAULT_AVATAR
        if (isNative && user?.avatar !== undefined) logo = user.avatar

        menuItems.push({
          key,
          tenantId,
          displayName,
          logo,
          tenantDomainName,
          isSameDomain,
          isNative,
          isLibrary,
          libraryId,
          url,
          username,
          wallsUsed,
          wallsLimit,
          remainingWallsQuota,
          canMakeWall,
          membershipTier,
        })
      })
    wallViewableAndVisibleLibrariesArray.value.forEach((library) => {
      const key: AccountKey = { type: 'library', id: library.id }
      const tenantId: TenantId = NATIVE_TENANT_ID
      const tenantDomainName = library.host ?? NATIVE_HOST
      const isSameDomain = currentDomainName === tenantDomainName
      const isNative = true
      const isLibrary = true
      const displayName: string = library.name
      const logo = library.avatar as string
      const fallbackLogo = `https://padlet.net/homes/${library.id}.png`
      const libraryId = library.id
      const slug = library.slug
      const url = library.dashboardUrl
      const canViewGallery = library.libraryAccess?.canViewGallery
      const wallsUsed: number = library.quota.wallsUsed
      const wallsLimit: number = library.quota.wallsLimit
      const remainingWallsQuota = library.quota.wallsLimit - library.quota.wallsUsed
      const canMakeWall = wallCreatableLibraryIds.value.includes(library.id)
      const membershipTier = library.membershipTier

      menuItems.push({
        key,
        tenantId,
        displayName,
        logo,
        fallbackLogo,
        tenantDomainName,
        isSameDomain,
        isNative,
        isLibrary,
        libraryId,
        url,
        slug,
        canViewGallery,
        wallsUsed,
        wallsLimit,
        remainingWallsQuota,
        canMakeWall,
        membershipTier,
      })
    })
    return sortBy(menuItems, 'tenantId')
  })

  function initialize(params?: { user: User }): void {
    user.value = params?.user ?? initialUser

    void useUserAccountsStore().fetchAccounts()
    void useUserAccountsStore().fetchWallViewableLibraries()
    void useUserAccountsStore().fetchWallCreatableLibraries()
  }

  function refreshWallQuotas(payload?: { forceQuotaRefresh: boolean }): void {
    void fetchAccounts({ forceQuotaRefresh: payload?.forceQuotaRefresh })
  }

  const fetchAccountsStatus = ref<FetchJsonStatus>(FetchJsonStatus.Fetching)
  const isFetchingAccounts = computed((): boolean => fetchAccountsStatus.value === FetchJsonStatus.Fetching)

  async function fetchAccounts(params?: { forceQuotaRefresh: boolean | undefined }): Promise<void> {
    const forceRefreshQuota = params?.forceQuotaRefresh === true ? 'true' : 'false'
    const urlParams = new URLSearchParams({
      forceQuotaRefresh: forceRefreshQuota,
    })
    try {
      if (isAppUsing('dashboardCaching')) {
        await fetchCachedQuery<JsonApiResponse<User | Library, Tenant>>({
          cacheKey: ['fetchAccounts', buildUserIdCacheKey(currentUser.value.id)],
          queryFn: async () => await fetchJson(`/api/1/accounts?${urlParams.toString()}`),
          onFetchSuccess: (response) => {
            const accountData = response.data as Array<JsonApiData<User | Library>>
            const tenantData = response.included as Array<JsonApiData<Tenant>>

            tenantData.forEach((item) => {
              if (item.type === 'tenant') {
                tenantsById.value = { ...tenantsById.value, [item.id]: item.attributes }
              }
            })

            accountData.forEach((account) => {
              if (account.type === 'user') {
                usersById.value = { ...usersById.value, [account.id]: account.attributes }
              }
              if (account.type === 'library') {
                librariesById.value = { ...librariesById.value, [account.id]: account.attributes }
              }
            })
            fetchAccountsStatus.value = FetchJsonStatus.Completed
          },
        })
      } else {
        const response = await fetchJson(`/api/1/accounts?${urlParams.toString()}`)
        const { data, included } = response
        included.forEach((item) => {
          if (item.type === 'tenant') {
            tenantsById.value = { ...tenantsById.value, [item.id]: item.attributes }
          }
        })

        data.forEach((account) => {
          if (account.type === 'user') {
            usersById.value = { ...usersById.value, [account.id]: account.attributes }
          }
          if (account.type === 'library') {
            librariesById.value = { ...librariesById.value, [account.id]: account.attributes }
          }
        })
        fetchAccountsStatus.value = FetchJsonStatus.Completed
      }
    } catch (error) {
      genericFetchError({ error, source: 'UserAccountsStoreFetchAccounts' })
      fetchAccountsStatus.value = FetchJsonStatus.Error
    }
  }

  /**
   * ==================== LIBRARY ACCOUNTS ====================
   */

  const librariesById = ref<Record<LibraryId, Library>>({})
  const wallViewableLibraryIds = ref<LibraryId[]>([])
  const wallCreatableLibraryIds = ref<LibraryId[]>([])

  const wallViewableAndVisibleLibrariesArray = computed((): Library[] =>
    librariesArray.value.filter((library) => library.visible),
  )
  const librariesArray = computed((): Library[] => Object.values(librariesById.value))

  async function fetchWallViewableLibraries(): Promise<void> {
    try {
      if (isAppUsing('dashboardCaching')) {
        await fetchCachedQuery<JsonApiResponse<Library>>({
          cacheKey: ['fetchWallViewableLibraries', buildUserIdCacheKey(currentUser.value.id)],
          queryFn: async () => await LibraryApi.fetchViewableLibraries({ userId: currentUser.value.id }),
          onFetchSuccess: (response) => {
            const nodes = response.data as Array<JsonAPIResource<Library>>
            const libraries = nodes.map((record) => record.attributes)
            libraries.forEach((library) => (librariesById.value = { ...librariesById.value, [library.id]: library }))
            const ids = libraries.map((library) => library.id)
            wallViewableLibraryIds.value = ids
          },
        })
      } else {
        const response = await LibraryApi.fetchViewableLibraries({ userId: currentUser.value.id })
        const nodes = response.data as Array<JsonAPIResource<Library>>
        const libraries = nodes.map((record) => record.attributes)
        libraries.forEach((library) => (librariesById.value = { ...librariesById.value, [library.id]: library }))
        const ids = libraries.map((library) => library.id)
        wallViewableLibraryIds.value = ids
      }
    } catch (error) {
      genericFetchError({ error, source: 'UserAccountsStoreFetchWallViewableLibraries' })
    }
  }

  const fetchWallCreatableLibrariesStatus = ref<FetchJsonStatus>(FetchJsonStatus.Fetching)
  const isFetchingWallCreatableLibraries = computed(
    () => fetchWallCreatableLibrariesStatus.value === FetchJsonStatus.Fetching,
  )

  async function fetchWallCreatableLibraries(): Promise<void> {
    try {
      if (isAppUsing('dashboardCaching')) {
        await fetchCachedQuery<JsonApiResponse<Library>>({
          cacheKey: ['fetchWallCreatableLibraries', buildUserIdCacheKey(currentUser.value.id)],
          queryFn: async () => await LibraryApi.fetchCreatableAndVisibleLibraries({ userId: currentUser.value.id }),
          onFetchSuccess: (response) => {
            const nodes = response.data as Array<JsonAPIResource<Library>>
            const libraries = nodes.map((record) => record.attributes)
            const ids = libraries.map((library) => library.id)
            wallCreatableLibraryIds.value = ids
            fetchWallCreatableLibrariesStatus.value = FetchJsonStatus.Completed
          },
        })
      } else {
        let response
        if (isAppUsing('dashboardSetting')) {
          response = await LibraryApi.fetchCreatableAndVisibleLibraries({ userId: currentUser.value.id })
        } else {
          response = await LibraryApi.fetchCreatableLibraries({ userId: currentUser.value.id })
        }
        const nodes = response.data as Array<JsonAPIResource<Library>>
        const libraries = nodes.map((record) => record.attributes)
        const ids = libraries.map((library) => library.id)
        wallCreatableLibraryIds.value = ids
        fetchWallCreatableLibrariesStatus.value = FetchJsonStatus.Completed
      }
    } catch (error) {
      genericFetchError({ error, source: 'UserAccountsStoreFetchWallCreatableLibraries' })
    }
  }

  return {
    /**
     * ==================== PERSONAL ACCOUNTS ====================
     */

    user,
    usersById,
    tenantsById,
    currentUser,
    currentTenant,
    usersArray,
    accountsMenuList,
    isFetchingAccounts,
    initialize,
    refreshWallQuotas,
    fetchAccounts,

    /**
     * ==================== LIBRARY ACCOUNTS ====================
     */

    librariesById,
    wallViewableLibraryIds,
    wallCreatableLibraryIds,
    isFetchingWallCreatableLibraries,
    wallViewableAndVisibleLibrariesArray,
    librariesArray,
    fetchWallViewableLibraries,
    fetchWallCreatableLibraries,
  }
})
